import { useId, useState } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Popover } from 'react-tiny-popover';
import {
  MIN_PRODUCT_SEARCH_LENGTH,
  usePlatformProducts,
} from '~/api/platformProducts';
import { useCombobox } from '~/hooks/useCombobox';
import {
  DEFAULT_SEARCH_DEBOUNCE_MS,
  useDebouncedSearchInput,
} from '~/hooks/useDebouncedSearchInput';
import type { PlatformProductsQuery } from '~/graphql/graphql';
import { cn, productLabel } from '~/utils';

export type CatalogProduct = NonNullable<
  PlatformProductsQuery['platformProducts']
>[number];

type CatalogProductSearchProps = Readonly<{
  testId: string;
  product: CatalogProduct | null;
  onProductChange: (product: CatalogProduct | null) => void;
  disabled?: boolean;
  className?: string;
}>;

/**
 * Catalog product typeahead. `onProductChange` receives the picked product, or null
 * once the buyer edits the search text of a picked product.
 *
 * @example
 *   <CatalogProductSearch testId="add-row" product={product} onProductChange={setProduct} />
 */
export function CatalogProductSearch({
  testId,
  product,
  onProductChange,
  disabled = false,
  className = 'w-56',
}: CatalogProductSearchProps) {
  const { t } = useTranslation('general');
  const searchId = useId();
  const { searchInputValue, activeSearch, handleSearchChange, setSearch } =
    useDebouncedSearchInput(
      DEFAULT_SEARCH_DEBOUNCE_MS,
      product ? productLabel(product) : ''
    );
  const [isListOpen, setIsListOpen] = useState(false);
  const { data, isFetching, error } = usePlatformProducts(activeSearch, {
    enabled: isListOpen,
  });

  const products = (data && data.platformProducts) ?? [];
  const isSearchLongEnough =
    activeSearch.trim().length >= MIN_PRODUCT_SEARCH_LENGTH;
  const isPanelOpen =
    isListOpen && isSearchLongEnough && (products.length > 0 || !isFetching);

  const pickProduct = (picked: CatalogProduct) => {
    onProductChange(picked);
    setSearch(productLabel(picked));
    setIsListOpen(false);
  };

  const {
    listboxId,
    safeActiveIndex,
    select,
    handleInputChange,
    handleKeyDown,
    inputProps,
    optionProps,
  } = useCombobox({
    items: products,
    term: activeSearch,
    isOpen: isPanelOpen,
    onRequestClose: () => setIsListOpen(false),
    onSelect: pickProduct,
  });

  return (
    <div className={cn('flex shrink-0 flex-col gap-1', className)}>
      <label htmlFor={searchId} className="text-xs font-medium text-neut-sec">
        {t('product_length_picker.product')}
      </label>
      <Popover
        isOpen={isPanelOpen}
        onClickOutside={() => setIsListOpen(false)}
        positions={['bottom', 'top']}
        align="start"
        containerStyle={{ zIndex: '1100' }}
        content={
          <ul
            id={listboxId}
            role="listbox"
            aria-label={t('product_length_picker.product')}
            className="mt-1 max-h-60 w-72 overflow-auto rounded-md border border-neut-border bg-neut-light py-1 shadow-lg"
          >
            {error && (
              <li
                className="px-3 py-2 text-sm text-neut-sec"
                data-testid={`${testId}-product-error`}
              >
                {t('product_length_picker.products_error')}
              </li>
            )}
            {!error && products.length === 0 && (
              <li
                className="px-3 py-2 text-sm text-neut-sec"
                data-testid={`${testId}-product-no-results`}
              >
                {t('product_length_picker.no_products')}
              </li>
            )}
            {products.map((option, index) => (
              <li
                key={option.id}
                {...optionProps(index)}
                data-testid={`${testId}-product-option-${option.id}`}
                className={cn(
                  'cursor-pointer px-3 py-2 text-sm text-neut-prim',
                  index === safeActiveIndex
                    ? 'bg-prim-50'
                    : 'hover:bg-neut-bg-alt'
                )}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => select(option)}
              >
                {productLabel(option)}
              </li>
            ))}
          </ul>
        }
      >
        <div
          className={cn(
            'flex items-center gap-2 rounded-md border border-neut-border bg-neut-bg px-2',
            disabled ? 'cursor-not-allowed opacity-50' : undefined
          )}
        >
          <Search size={14} className="text-neut-sec" aria-hidden="true" />
          <input
            id={searchId}
            data-testid={`${testId}-product-search`}
            className="w-full bg-transparent py-2 text-sm outline-none disabled:cursor-not-allowed"
            placeholder={t('product_length_picker.product_placeholder')}
            value={searchInputValue}
            disabled={disabled}
            {...inputProps}
            onFocus={() => setIsListOpen(!product)}
            onKeyDown={handleKeyDown}
            onChange={(event) => {
              if (product) {
                onProductChange(null);
              }
              setIsListOpen(true);
              handleInputChange();
              handleSearchChange(event);
            }}
          />
        </div>
      </Popover>
    </div>
  );
}
